import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export interface DragItem {
  id: string;
  index: number;
  type: string;
  data?: Record<string, unknown>;
}

export type DragState = 'idle' | 'dragging' | 'over';

export interface SortableContextValue {
  items: string[];
  activeId: string | null;
  overId: string | null;
  onDragStart: (id: string) => void;
  onDragOver: (id: string) => void;
  onDragEnd: () => void;
  onDrop: (sourceId: string, targetId: string) => void;
  disabled: boolean;
  handlePosition: 'start' | 'end' | 'none';
}

const SortableContext = React.createContext<SortableContextValue | null>(null);

function useSortableContext(): SortableContextValue {
  const context = React.useContext(SortableContext);
  if (!context) {
    throw new Error('Sortable components must be used within a SortableList');
  }
  return context;
}

// ============================================================================
// SortableList Component
// ============================================================================

export interface SortableListProps<T> extends React.HTMLAttributes<HTMLDivElement> {
  items: T[];
  onReorder: (items: T[]) => void;
  keyExtractor: (item: T) => string;
  renderItem: (item: T, index: number, dragHandleProps: DragHandleProps) => React.ReactNode;
  direction?: 'vertical' | 'horizontal';
  disabled?: boolean;
  handlePosition?: 'start' | 'end' | 'none';
  gap?: 'none' | 'sm' | 'md' | 'lg';
}

export function SortableList<T>({
  items,
  onReorder,
  keyExtractor,
  renderItem,
  direction = 'vertical',
  disabled = false,
  handlePosition = 'start',
  gap = 'md',
  className,
  ...props
}: SortableListProps<T>) {
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [overId, setOverId] = React.useState<string | null>(null);

  const itemIds = items.map(keyExtractor);

  const onDragStart = React.useCallback((id: string) => {
    if (disabled) return;
    setActiveId(id);
  }, [disabled]);

  const onDragOver = React.useCallback((id: string) => {
    if (disabled || !activeId) return;
    setOverId(id);
  }, [disabled, activeId]);

  const onDragEnd = React.useCallback(() => {
    setActiveId(null);
    setOverId(null);
  }, []);

  const onDrop = React.useCallback(
    (sourceId: string, targetId: string) => {
      if (disabled || sourceId === targetId) return;

      const sourceIndex = itemIds.indexOf(sourceId);
      const targetIndex = itemIds.indexOf(targetId);

      if (sourceIndex === -1 || targetIndex === -1) return;

      const newItems = [...items];
      const [removed] = newItems.splice(sourceIndex, 1);
      newItems.splice(targetIndex, 0, removed);

      onReorder(newItems);
      onDragEnd();
    },
    [items, itemIds, disabled, onReorder, onDragEnd]
  );

  const gapClasses = {
    none: 'gap-0',
    sm: 'gap-1',
    md: 'gap-2',
    lg: 'gap-4',
  };

  const contextValue: SortableContextValue = {
    items: itemIds,
    activeId,
    overId,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDrop,
    disabled,
    handlePosition,
  };

  return (
    <SortableContext.Provider value={contextValue}>
      <div
        role="list"
        aria-label="Sortable list"
        className={clsx(
          'flex',
          direction === 'vertical' ? 'flex-col' : 'flex-row flex-wrap',
          gapClasses[gap],
          className
        )}
        {...props}
      >
        {items.map((item, index) => {
          const id = keyExtractor(item);
          const dragHandleProps: DragHandleProps = {
            id,
            disabled,
          };
          return (
            <SortableItem key={id} id={id}>
              {renderItem(item, index, dragHandleProps)}
            </SortableItem>
          );
        })}
      </div>
    </SortableContext.Provider>
  );
}

// ============================================================================
// SortableItem Component
// ============================================================================

export interface SortableItemProps extends React.HTMLAttributes<HTMLDivElement> {
  id: string;
  children: React.ReactNode;
}

export function SortableItem({
  id,
  children,
  className,
  ...props
}: SortableItemProps) {
  const { activeId, overId, onDragOver, onDrop, disabled } = useSortableContext();

  const isDragging = activeId === id;
  const isOver = overId === id && activeId !== id;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      onDragOver(id);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (activeId && !disabled) {
      onDrop(activeId, id);
    }
  };

  return (
    <div
      role="listitem"
      className={clsx(
        'relative transition-all duration-200',
        isDragging && 'opacity-50 scale-95',
        isOver && 'ring-2 ring-blue-500 ring-offset-2',
        className
      )}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      aria-grabbed={isDragging}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// DragHandle Component
// ============================================================================

export interface DragHandleProps {
  id: string;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export function DragHandle({
  id,
  disabled = false,
  className,
  children,
}: DragHandleProps) {
  const { onDragStart, onDragEnd, disabled: listDisabled } = useSortableContext();

  const isDisabled = disabled || listDisabled;

  const handleDragStart = (e: React.DragEvent) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    onDragStart(id);
  };

  const handleDragEnd = () => {
    onDragEnd();
  };

  return (
    <div
      draggable={!isDisabled}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={clsx(
        'cursor-grab active:cursor-grabbing touch-none select-none',
        isDisabled && 'cursor-not-allowed opacity-50',
        className
      )}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-label="Drag handle"
      aria-disabled={isDisabled}
    >
      {children || (
        <svg
          className="w-5 h-5 text-gray-400 dark:text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 8h16M4 16h16"
          />
        </svg>
      )}
    </div>
  );
}

// ============================================================================
// DropZone Component
// ============================================================================

export interface DropZoneProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrop'> {
  onDrop: (item: DragItem) => void;
  accept?: string[];
  children: React.ReactNode;
  activeClassName?: string;
}

export function DropZone({
  onDrop,
  accept,
  children,
  activeClassName = 'bg-blue-50 border-blue-500',
  className,
  ...props
}: DropZoneProps) {
  const [isOver, setIsOver] = React.useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const type = e.dataTransfer.types[0];
    if (!accept || accept.includes(type)) {
      e.dataTransfer.dropEffect = 'move';
      setIsOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);

    const data = e.dataTransfer.getData('text/plain');
    try {
      const item = JSON.parse(data) as DragItem;
      onDrop(item);
    } catch {
      onDrop({ id: data, index: 0, type: 'unknown' });
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={clsx(
        'border-2 border-dashed rounded-lg transition-colors',
        isOver ? activeClassName : 'border-gray-300 dark:border-gray-600',
        className
      )}
      role="region"
      aria-label="Drop zone"
      aria-dropeffect="move"
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// DraggableCard Component
// ============================================================================

export interface DraggableCardProps extends React.HTMLAttributes<HTMLDivElement> {
  id: string;
  type?: string;
  data?: Record<string, unknown>;
  disabled?: boolean;
  children: React.ReactNode;
}

export function DraggableCard({
  id,
  type = 'card',
  data,
  disabled = false,
  children,
  className,
  ...props
}: DraggableCardProps) {
  const [isDragging, setIsDragging] = React.useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    const item: DragItem = { id, index: 0, type, data };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify(item));
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <div
      draggable={!disabled}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={clsx(
        'p-4 bg-white dark:bg-gray-800 rounded-lg shadow cursor-grab active:cursor-grabbing',
        isDragging && 'opacity-50',
        disabled && 'cursor-not-allowed opacity-60',
        className
      )}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-grabbed={isDragging}
      aria-disabled={disabled}
      {...props}
    >
      {children}
    </div>
  );
}

// ============================================================================
// ReorderList Component (Simplified API)
// ============================================================================

export interface ReorderItem {
  id: string;
  content: React.ReactNode;
}

export interface ReorderListProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  items: ReorderItem[];
  onChange: (items: ReorderItem[]) => void;
  disabled?: boolean;
  variant?: 'default' | 'bordered' | 'card';
}

export function ReorderList({
  items,
  onChange,
  disabled = false,
  variant = 'default',
  className,
  ...props
}: ReorderListProps) {
  const variantClasses = {
    default: 'bg-gray-50 dark:bg-gray-900',
    bordered: 'border border-gray-200 dark:border-gray-700',
    card: 'bg-white dark:bg-gray-800 shadow rounded-lg',
  };

  return (
    <SortableList
      items={items}
      onReorder={onChange}
      keyExtractor={(item) => item.id}
      renderItem={(item, _index, dragHandleProps) => (
        <div
          className={clsx(
            'flex items-center gap-3 px-4 py-3',
            variantClasses[variant],
            !disabled && 'hover:bg-gray-100 dark:hover:bg-gray-700'
          )}
        >
          <DragHandle {...dragHandleProps} />
          <div className="flex-1">{item.content}</div>
        </div>
      )}
      disabled={disabled}
      className={className}
      {...props}
    />
  );
}

// ============================================================================
// SortableKanban Component
// ============================================================================

export interface KanbanItem {
  id: string;
  columnId: string;
  content: React.ReactNode;
}

export interface KanbanColumn {
  id: string;
  title: string;
}

export interface SortableKanbanProps extends React.HTMLAttributes<HTMLDivElement> {
  columns: KanbanColumn[];
  items: KanbanItem[];
  onItemMove: (itemId: string, toColumnId: string, toIndex: number) => void;
  renderItem?: (item: KanbanItem) => React.ReactNode;
  columnClassName?: string;
  itemClassName?: string;
}

export function SortableKanban({
  columns,
  items,
  onItemMove,
  renderItem,
  columnClassName,
  itemClassName,
  className,
  ...props
}: SortableKanbanProps) {
  const handleDrop = (columnId: string) => (dragItem: DragItem) => {
    const columnItems = items.filter((i) => i.columnId === columnId);
    onItemMove(dragItem.id, columnId, columnItems.length);
  };

  return (
    <div
      className={clsx('flex gap-4 overflow-x-auto p-4', className)}
      role="region"
      aria-label="Kanban board"
      {...props}
    >
      {columns.map((column) => {
        const columnItems = items.filter((item) => item.columnId === column.id);

        return (
          <div
            key={column.id}
            className={clsx(
              'flex-shrink-0 w-72 bg-gray-100 dark:bg-gray-800 rounded-lg',
              columnClassName
            )}
          >
            <div className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700">
              {column.title}
              <span className="ml-2 text-sm text-gray-400 dark:text-gray-500">({columnItems.length})</span>
            </div>
            <DropZone
              onDrop={handleDrop(column.id)}
              className="p-2 min-h-[200px] border-0"
            >
              <div className="space-y-2">
                {columnItems.map((item) => (
                  <DraggableCard
                    key={item.id}
                    id={item.id}
                    type="kanban-item"
                    data={{ columnId: column.id }}
                    className={itemClassName}
                  >
                    {renderItem ? renderItem(item) : item.content}
                  </DraggableCard>
                ))}
                {columnItems.length === 0 && (
                  <div className="text-center py-8 text-gray-400 text-sm dark:text-gray-500">
                    Drop items here
                  </div>
                )}
              </div>
            </DropZone>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// OrderedList Component
// ============================================================================

export interface OrderedListProps extends Omit<React.HTMLAttributes<HTMLOListElement>, 'onChange'> {
  items: ReorderItem[];
  onChange: (items: ReorderItem[]) => void;
  disabled?: boolean;
  showNumbers?: boolean;
}

export function OrderedList({
  items,
  onChange,
  disabled = false,
  showNumbers = true,
  className,
  ...props
}: OrderedListProps) {
  return (
    <ol className={clsx('list-none', className)} {...props}>
      <SortableList
        items={items}
        onReorder={onChange}
        keyExtractor={(item) => item.id}
        renderItem={(item, index, dragHandleProps) => (
          <li className="flex items-center gap-3 py-2 px-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
            <DragHandle {...dragHandleProps} />
            {showNumbers && (
              <span className="w-6 h-6 flex items-center justify-center bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full text-sm font-medium">
                {index + 1}
              </span>
            )}
            <div className="flex-1">{item.content}</div>
          </li>
        )}
        disabled={disabled}
      />
    </ol>
  );
}

// ============================================================================
// useSortable Hook
// ============================================================================

export interface UseSortableOptions<T> {
  items: T[];
  keyExtractor: (item: T) => string;
  onReorder?: (items: T[]) => void;
}

export interface UseSortableReturn<T> {
  items: T[];
  activeId: string | null;
  move: (fromIndex: number, toIndex: number) => void;
  moveToStart: (index: number) => void;
  moveToEnd: (index: number) => void;
  swap: (indexA: number, indexB: number) => void;
  setItems: React.Dispatch<React.SetStateAction<T[]>>;
  reset: () => void;
  isDragging: boolean;
  getItemProps: (id: string) => {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragEnd: () => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
}

export function useSortable<T>({
  items: initialItems,
  keyExtractor,
  onReorder,
}: UseSortableOptions<T>): UseSortableReturn<T> {
  const [items, setItems] = React.useState<T[]>(initialItems);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const originalItems = React.useRef<T[]>(initialItems);

  React.useEffect(() => {
    setItems(initialItems);
    originalItems.current = initialItems;
  }, [initialItems]);

  const move = React.useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;
      if (fromIndex < 0 || fromIndex >= items.length) return;
      if (toIndex < 0 || toIndex >= items.length) return;

      const newItems = [...items];
      const [removed] = newItems.splice(fromIndex, 1);
      newItems.splice(toIndex, 0, removed);

      setItems(newItems);
      onReorder?.(newItems);
    },
    [items, onReorder]
  );

  const moveToStart = React.useCallback(
    (index: number) => move(index, 0),
    [move]
  );

  const moveToEnd = React.useCallback(
    (index: number) => move(index, items.length - 1),
    [move, items.length]
  );

  const swap = React.useCallback(
    (indexA: number, indexB: number) => {
      if (indexA === indexB) return;
      if (indexA < 0 || indexA >= items.length) return;
      if (indexB < 0 || indexB >= items.length) return;

      const newItems = [...items];
      [newItems[indexA], newItems[indexB]] = [newItems[indexB], newItems[indexA]];

      setItems(newItems);
      onReorder?.(newItems);
    },
    [items, onReorder]
  );

  const reset = React.useCallback(() => {
    setItems(originalItems.current);
    onReorder?.(originalItems.current);
  }, [onReorder]);

  const getItemProps = React.useCallback(
    (id: string) => ({
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', id);
        setActiveId(id);
      },
      onDragEnd: () => {
        setActiveId(null);
      },
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        const sourceId = e.dataTransfer.getData('text/plain');
        if (sourceId === id) return;

        const sourceIndex = items.findIndex((item) => keyExtractor(item) === sourceId);
        const targetIndex = items.findIndex((item) => keyExtractor(item) === id);

        if (sourceIndex !== -1 && targetIndex !== -1) {
          move(sourceIndex, targetIndex);
        }
        setActiveId(null);
      },
    }),
    [items, keyExtractor, move]
  );

  return {
    items,
    activeId,
    move,
    moveToStart,
    moveToEnd,
    swap,
    setItems,
    reset,
    isDragging: activeId !== null,
    getItemProps,
  };
}

// ============================================================================
// useDrag Hook
// ============================================================================

export interface UseDragOptions {
  type: string;
  item: DragItem;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

export interface UseDragReturn {
  isDragging: boolean;
  dragProps: {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragEnd: () => void;
  };
}

export function useDrag({
  type,
  item,
  onDragStart,
  onDragEnd,
}: UseDragOptions): UseDragReturn {
  const [isDragging, setIsDragging] = React.useState(false);

  const dragProps = {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData(type, JSON.stringify(item));
      setIsDragging(true);
      onDragStart?.();
    },
    onDragEnd: () => {
      setIsDragging(false);
      onDragEnd?.();
    },
  };

  return { isDragging, dragProps };
}

// ============================================================================
// useDrop Hook
// ============================================================================

export interface UseDropOptions {
  accept: string[];
  onDrop: (item: DragItem) => void;
  onDragOver?: () => void;
  onDragLeave?: () => void;
}

export interface UseDropReturn {
  isOver: boolean;
  dropProps: {
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: () => void;
    onDrop: (e: React.DragEvent) => void;
  };
}

export function useDrop({
  accept,
  onDrop,
  onDragOver,
  onDragLeave,
}: UseDropOptions): UseDropReturn {
  const [isOver, setIsOver] = React.useState(false);

  const dropProps = {
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      const hasAcceptedType = accept.some((type) => e.dataTransfer.types.includes(type));
      if (hasAcceptedType) {
        e.dataTransfer.dropEffect = 'move';
        setIsOver(true);
        onDragOver?.();
      }
    },
    onDragLeave: () => {
      setIsOver(false);
      onDragLeave?.();
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setIsOver(false);

      for (const type of accept) {
        const data = e.dataTransfer.getData(type);
        if (data) {
          try {
            const item = JSON.parse(data) as DragItem;
            onDrop(item);
          } catch {
            onDrop({ id: data, index: 0, type });
          }
          break;
        }
      }
    },
  };

  return { isOver, dropProps };
}
